/**
 * exportCertificatePDF — renders CertificateA4 into an offscreen React root
 * at real A4-landscape pixel dimensions, then rasterises it into a PDF that
 * is pixel-identical to the on-screen preview.
 *
 * Why not just cloneNode the on-screen DOM?
 *   CertificateA4 uses <FitText> which measures its own container in
 *   pixels inside a useLayoutEffect. A tiny 400px preview card ends up
 *   with tiny absolute font-sizes baked into the DOM. Cloning that node
 *   into a 1122px offscreen stage would freeze those small font sizes
 *   and the PDF would look shrunken. By mounting a fresh React tree at
 *   the target size, FitText re-runs and the name/title fill the page.
 */

import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import CertificateA4 from "./CertificateA4";
import { getInlinedFontsCss, injectInlinedFonts, ensureFontsLoaded } from "../report/inlineFonts";

const A4_WIDTH_MM = 297;
const A4_HEIGHT_MM = 210;

// 1122 × 793 px ≈ 96 DPI A4 landscape — safe base geometry.
const BASE_WIDTH_PX = 1122;
const BASE_HEIGHT_PX = Math.round(BASE_WIDTH_PX * (A4_HEIGHT_MM / A4_WIDTH_MM));

// html2canvas oversamples for print-grade output (≈ 288 DPI).
const RENDER_SCALE = 3;

function mountOffscreen(props) {
  const stage = document.createElement("div");
  stage.setAttribute("data-certificate-stage", "true");
  Object.assign(stage.style, {
    position: "fixed",
    left: "-99999px",
    top: "0",
    width: `${BASE_WIDTH_PX}px`,
    height: `${BASE_HEIGHT_PX}px`,
    background: "#ffffff",
    pointerEvents: "none",
    zIndex: "-1",
    overflow: "hidden",
  });
  document.body.appendChild(stage);

  const root = createRoot(stage);
  root.render(<CertificateA4 {...props} />);
  return { stage, root };
}

async function waitForLayout() {
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (_) {
      /* ignore */
    }
  }
  // Two RAFs = one frame for React commit, one for FitText's layout effect
  // to run and re-flow the DOM. A short settle timeout guards slow devices.
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  await new Promise((r) => setTimeout(r, 60));
}

export async function exportCertificatePDF(certProps, filename = "certificate.pdf") {
  // Before mounting, not after: FitText measures its container in a layout
  // effect the moment this mounts. If the webfonts have not landed yet (cold
  // cache / first visit) it sizes the name and title against fallback metrics,
  // and the exported PDF comes out clipped and unlike the on-screen card.
  await ensureFontsLoaded();
  const { stage, root } = mountOffscreen(certProps || {});
  try {
    await waitForLayout();

    // The A4 node is the first child rendered by CertificateA4.
    const node = stage.firstElementChild;
    if (!node) throw new Error("Certificate failed to mount");

    const inlinedFontsCss = await getInlinedFontsCss();

    const canvas = await html2canvas(node, {
      scale: RENDER_SCALE,
      backgroundColor: "#FBF9F3",
      useCORS: true,
      logging: false,
      width: BASE_WIDTH_PX,
      height: BASE_HEIGHT_PX,
      windowWidth: BASE_WIDTH_PX,
      windowHeight: BASE_HEIGHT_PX,
      onclone: (clonedDoc) => injectInlinedFonts(clonedDoc, inlinedFontsCss),
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    pdf.addImage(imgData, "PNG", 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM, undefined, "FAST");
    pdf.save(filename);
  } finally {
    try {
      root.unmount();
    } catch (_) {
      /* ignore */
    }
    if (stage.parentNode) stage.parentNode.removeChild(stage);
  }
}

/** Print the certificate at real A4 landscape dimensions.
 *
 *  Uses an isolated iframe via `srcdoc` (not `document.write`) so the
 *  print surface runs in its own document origin and cannot be reached
 *  by any script in the parent page. Certificate props are only ever
 *  written via `node.outerHTML`, which React has already HTML-encoded.
 */
export async function printCertificate(certProps) {
  await ensureFontsLoaded();
  const { stage, root } = mountOffscreen(certProps || {});
  try {
    await waitForLayout();
    const node = stage.firstElementChild;
    if (!node) return;

    const styles = Array.from(document.styleSheets)
      .map((s) => {
        try {
          return Array.from(s.cssRules || []).map((r) => r.cssText).join("");
        } catch (err) {
          // Cross-origin stylesheets throw when enumerating cssRules —
          // safe to skip, they don't apply to the certificate DOM anyway.
          return "";
        }
      })
      .join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Certificate</title>
      <style>${styles}</style>
      <style>
        @page { size: A4 landscape; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body { display: flex; align-items: center; justify-content: center; }
        .print-frame { width: 297mm; height: 210mm; overflow: hidden; }
      </style></head><body>
      <div class="print-frame">${node.outerHTML}</div>
      </body></html>`;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    Object.assign(iframe.style, {
      position: "fixed",
      right: "0",
      bottom: "0",
      width: "0",
      height: "0",
      border: "0",
    });
    // `sandbox` restricts the iframe: only same-origin resolution for
    // stylesheets, no scripts, no top-level nav.
    iframe.setAttribute("sandbox", "allow-same-origin allow-modals");
    iframe.srcdoc = html;
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };
    iframe.addEventListener("load", () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (err) {
        console.warn("printCertificate: contentWindow.print failed", err);
      }
      // Give the browser a moment to open its print dialog before we tear
      // the iframe down. Some browsers block print() if the DOM disappears
      // immediately.
      setTimeout(cleanup, 2000);
    });
  } finally {
    try {
      root.unmount();
    } catch (err) {
      // React root can be torn down concurrently by another export — safe
      // to ignore; the stage cleanup below is what matters.
    }
    if (stage.parentNode) stage.parentNode.removeChild(stage);
  }
}
